import { AuthService } from '@/services/authService';
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig<unknown> {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

export abstract class BaseService {
  protected apiClient;

  constructor(basePath: string, isBlob = false) {
    this.apiClient = axios.create({
      baseURL: `${import.meta.env.VITE_API_BASE_URL || '/api'}${basePath}`,
      withCredentials: true,
      paramsSerializer: {
        indexes: null
      }
    });

    // Request Interceptor
    this.apiClient.interceptors.request.use((config) => {
      const headers = this.getHeaders();
      Object.keys(headers).forEach((header) => {
        // if (header === 'Content-Type' && config.headers[header])
        config.headers[header] = headers[header];
        config.withCredentials = true;
      });
      if (isBlob) config.responseType = 'blob';
      return config;
    });

    // Response Interceptor
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: ExtendedAxiosRequestConfig = error.config || {} as ExtendedAxiosRequestConfig;
        const isUnauthorized: boolean | undefined = error.response && error.response.status === 401;

        if ((isUnauthorized) && originalRequest._retry !== true) {

          // CASE A: Refresh is already happening
          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.apiClient(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          // CASE B: First 401 - Start the refresh
          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const authService = new AuthService();
            //if (location.search.includes('code=')) return;
            await authService.refreshToken();

            // Process the queue with the new token
            processQueue(null);

            return this.apiClient(originalRequest);

          } catch (refreshError) {
            // Refresh failed (token expired or invalid)
            processQueue(refreshError);

            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  protected getHeaders(skipTenant?: boolean) {
    const headers: Record<string, string> = {
      // 'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }

    if (!skipTenant) {
      const authService = new AuthService();
      const tenant = authService.getTenant()?.toUpperCase() || 'SPRINT'
      headers['X-Tenant'] = tenant
    }

    return headers
  }
}
